import {
  forwardRef,
  memo,
  useState,
  useCallback,
  useRef,
  useEffect,
  useMemo,
  useImperativeHandle,
} from 'react'
import {
  ResponsiveContainer,
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Brush,
  Label,
  ReferenceArea,
} from 'recharts'
import type { SpeedChartProps } from './types'
import { downsampleChartData } from './downsample-chart-data'

const ZOOM_STEP = 0.2

export interface SpeedChartPlotHandle {
  toggleLock: () => void
  zoomIn: () => void
  zoomOut: () => void
  resetZoom: () => void
}

type SpeedChartPlotProps = Omit<
  SpeedChartProps,
  | 'title'
  | 'collapsible'
  | 'defaultCollapsed'
  | 'onCollapse'
  | 'dateTime'
  | 'metrics'
  | 'currentSpeed'
  | 'currentDistance'
  | 'speedUnit'
  | 'distanceUnit'
  | 'className'
>

interface TooltipEntry {
  name?: string | number
  value?: number | string | readonly (number | string)[]
  color?: string
}

interface DefaultTooltipProps {
  active?: boolean
  payload?: readonly TooltipEntry[]
  label?: unknown
  labelFormatter?: (label: unknown) => React.ReactNode
  formatter?: (value: unknown, name: string) => [string, string] | string
}

function DefaultTooltip({
  active,
  payload,
  label,
  labelFormatter,
  formatter,
}: DefaultTooltipProps) {
  if (!active || !payload?.length) return null

  const displayLabel = labelFormatter ? labelFormatter(label) : label

  return (
    <div className="bg-white border border-gray-200 rounded-lg shadow-lg px-3 py-2.5 text-xs min-w-28">
      {displayLabel !== undefined && displayLabel !== '' && (
        <p className="font-semibold text-gray-700 mb-1.5 border-b border-gray-100 pb-1.5">
          {String(displayLabel)}
        </p>
      )}
      <div className="space-y-1.5">
        {payload.map((entry, i) => {
          const raw = formatter ? formatter(entry.value, String(entry.name ?? '')) : undefined
          const displayValue = Array.isArray(raw) ? raw[0] : (raw ?? String(entry.value ?? ''))
          const displayName = Array.isArray(raw) ? raw[1] : String(entry.name ?? '')
          return (
            <div key={i} className="flex items-center gap-2">
              <span
                className="w-2 h-2 rounded-full shrink-0"
                style={{ backgroundColor: entry.color }}
              />
              {displayName && <span className="text-gray-500">{displayName}:</span>}
              <span className="font-medium text-gray-900 ml-auto pl-2">{displayValue}</span>
            </div>
          )
        })}
      </div>
    </div>
  )
}

const SpeedChartPlotComponent = forwardRef<SpeedChartPlotHandle, SpeedChartPlotProps>(function SpeedChartPlotComponent({
  data,
  dataKey = 'speed',
  xAxisKey = 'time',
  yAxisLabel = 'Speed (Km/h)',
  xAxisLabel,
  yAxisDomain = ['auto', 'auto'],
  yAxisWidth = 72,
  yAxisFormatter,
  xAxisFormatter,
  color = '#2563eb',
  lineWidth = 2,
  showDots = false,
  curveType = 'linear',
  showGrid = true,
  showTooltip = true,
  customTooltip: CustomTooltip,
  tooltipLabelFormatter,
  tooltipFormatter,
  showBrush = true,
  brushHeight = 32,
  onHoverPoint,
  onDotClick,
  onDotHover,
  onLockChange,
  height = 260,
  margin = { top: 8, right: 16, bottom: 8, left: 8 },
  onLockedChange,
}, ref) {
  const [locked, setLocked] = useState(false)
  const [brushStart, setBrushStart] = useState(0)
  const [brushEnd, setBrushEnd] = useState(Math.max(0, data.length - 1))
  const [selectionStart, setSelectionStart] = useState<number | null>(null)
  const [selectionEnd, setSelectionEnd] = useState<number | null>(null)

  const selectionStartRef = useRef<number | null>(null)
  const selectionEndRef = useRef<number | null>(null)
  const lastHoverIndexRef = useRef<number | null>(null)
  const hoverRafRef = useRef<number | null>(null)
  const pendingHoverRef = useRef<{
    point: Record<string, unknown> | null
    index: number | null
  } | null>(null)
  const selectionPaintRafRef = useRef<number | null>(null)
  const onHoverPointRef = useRef(onHoverPoint)
  const onDotHoverRef = useRef(onDotHover)
  const sourceDataRef = useRef(data)
  const toSourceIndexRef = useRef<(chartIndex: number) => number>((i) => i)

  useEffect(() => {
    onHoverPointRef.current = onHoverPoint
    onDotHoverRef.current = onDotHover
  }, [onDotHover, onHoverPoint])

  useEffect(() => {
    sourceDataRef.current = data
  }, [data])

  const { displayData, toSourceIndex } = useMemo(() => downsampleChartData(data), [data])
  toSourceIndexRef.current = toSourceIndex

  const displayPointCount = displayData.length

  useEffect(() => {
    setBrushEnd(Math.max(0, displayPointCount - 1))
    setBrushStart(0)
    lastHoverIndexRef.current = null
  }, [displayPointCount, data])

  useEffect(() => {
    return () => {
      if (hoverRafRef.current != null) window.cancelAnimationFrame(hoverRafRef.current)
      if (selectionPaintRafRef.current != null) {
        window.cancelAnimationFrame(selectionPaintRafRef.current)
      }
    }
  }, [])

  const resolveSourcePoint = useCallback((chartIndex: number | null) => {
    if (chartIndex === null) return { point: null as Record<string, unknown> | null, index: null }
    const sourceIndex = toSourceIndexRef.current(chartIndex)
    const source = sourceDataRef.current
    const point = source[sourceIndex] as Record<string, unknown> | undefined
    return {
      point: point ?? null,
      index: point ? sourceIndex : null,
    }
  }, [])

  const emitHoverPoint = useCallback(
    (chartIndex: number | null) => {
      if (chartIndex === lastHoverIndexRef.current) return
      lastHoverIndexRef.current = chartIndex

      const { point, index } = resolveSourcePoint(chartIndex)
      pendingHoverRef.current = { point, index }
      if (hoverRafRef.current != null) return

      hoverRafRef.current = window.requestAnimationFrame(() => {
        hoverRafRef.current = null
        const pending = pendingHoverRef.current
        if (!pending) return
        onHoverPointRef.current?.(pending.point, pending.index)
        if (pending.point) onDotHoverRef.current?.(pending.point)
      })
    },
    [resolveSourcePoint],
  )

  const scheduleSelectionPaint = useCallback(() => {
    if (selectionPaintRafRef.current != null) return
    selectionPaintRafRef.current = window.requestAnimationFrame(() => {
      selectionPaintRafRef.current = null
      setSelectionEnd(selectionEndRef.current)
    })
  }, [])

  const totalPoints = displayPointCount
  const selectionMin =
    selectionStart !== null && selectionEnd !== null ? Math.min(selectionStart, selectionEnd) : null
  const selectionMax =
    selectionStart !== null && selectionEnd !== null ? Math.max(selectionStart, selectionEnd) : null

  const getActiveTooltipIndex = useCallback(
    (state: unknown): number | null => {
      if (!state || typeof state !== 'object') return null
      const raw = (state as { activeTooltipIndex?: unknown }).activeTooltipIndex
      const local =
        typeof raw === 'number' ? raw : typeof raw === 'string' ? Number(raw) : Number.NaN
      if (!Number.isFinite(local)) return null
      const absolute = brushStart + local
      return Math.min(Math.max(absolute, 0), Math.max(0, totalPoints - 1))
    },
    [brushStart, totalPoints],
  )

  const handleBrushChange = useCallback(
    (range: { startIndex?: number; endIndex?: number }) => {
      if (locked) return
      if (range.startIndex !== undefined) setBrushStart(range.startIndex)
      if (range.endIndex !== undefined) setBrushEnd(range.endIndex)
    },
    [locked],
  )

  const handleZoomIn = useCallback(() => {
    if (locked) return
    const range = brushEnd - brushStart
    const delta = Math.max(1, Math.floor(range * ZOOM_STEP))
    setBrushStart(Math.min(brushStart + delta, brushEnd - 1))
    setBrushEnd(Math.max(brushEnd - delta, brushStart + 1))
  }, [locked, brushStart, brushEnd])

  const handleZoomOut = useCallback(() => {
    if (locked) return
    const range = brushEnd - brushStart
    const delta = Math.max(1, Math.floor(range * ZOOM_STEP))
    setBrushStart(Math.max(0, brushStart - delta))
    setBrushEnd(Math.min(totalPoints - 1, brushEnd + delta))
  }, [locked, brushStart, brushEnd, totalPoints])

  const handleReset = useCallback(() => {
    if (locked) return
    setSelectionStart(null)
    setSelectionEnd(null)
    setBrushStart(0)
    setBrushEnd(Math.max(0, totalPoints - 1))
  }, [locked, totalPoints])

  const handleLockChart = useCallback(() => {
    setLocked((currentLocked) => {
      const nextLocked = !currentLocked
      onLockedChange?.(nextLocked)
      onLockChange?.(nextLocked, null, null)
      return nextLocked
    })
  }, [onLockChange, onLockedChange])

  useImperativeHandle(
    ref,
    () => ({
      toggleLock: handleLockChart,
      zoomIn: handleZoomIn,
      zoomOut: handleZoomOut,
      resetZoom: handleReset,
    }),
    [handleLockChart, handleReset, handleZoomIn, handleZoomOut],
  )

  const handleChartMouseMove = useCallback(
    (state: unknown) => {
      const activeIndex = getActiveTooltipIndex(state)

      if (selectionStartRef.current !== null && activeIndex !== null && !locked) {
        if (selectionEndRef.current !== activeIndex) {
          selectionEndRef.current = activeIndex
          scheduleSelectionPaint()
        }
      }

      if (locked || !onHoverPointRef.current) return

      if (activeIndex === lastHoverIndexRef.current) return
      emitHoverPoint(activeIndex)
    },
    [emitHoverPoint, getActiveTooltipIndex, locked, scheduleSelectionPaint],
  )

  const handleMouseLeave = useCallback(() => {
    selectionStartRef.current = null
    selectionEndRef.current = null
    setSelectionStart(null)
    setSelectionEnd(null)
    if (locked) return
    lastHoverIndexRef.current = null
    pendingHoverRef.current = { point: null, index: null }
    if (hoverRafRef.current != null) {
      window.cancelAnimationFrame(hoverRafRef.current)
      hoverRafRef.current = null
    }
    onHoverPointRef.current?.(null, null)
  }, [locked])

  const handleChartMouseDown = useCallback(
    (state: unknown) => {
      if (locked) return
      const activeIndex = getActiveTooltipIndex(state)
      if (activeIndex === null) return
      selectionStartRef.current = activeIndex
      selectionEndRef.current = activeIndex
      setSelectionStart(activeIndex)
      setSelectionEnd(activeIndex)
    },
    [getActiveTooltipIndex, locked],
  )

  const handleChartMouseUp = useCallback(() => {
    if (locked) return

    const startRaw = selectionStartRef.current
    const endRaw = selectionEndRef.current

    selectionStartRef.current = null
    selectionEndRef.current = null
    setSelectionStart(null)
    setSelectionEnd(null)

    if (startRaw === null || endRaw === null) return

    const start = Math.min(startRaw, endRaw)
    const end = Math.max(startRaw, endRaw)

    if (end <= start) {
      const { point, index } = resolveSourcePoint(start)
      if (!point || index === null) return
      setLocked(true)
      onLockedChange?.(true)
      onLockChange?.(true, point, index)
      onDotClick?.(point, index)
      return
    }

    setBrushStart(start)
    setBrushEnd(end)
  }, [locked, onDotClick, onLockChange, onLockedChange, resolveSourcePoint])

  const handleDotClick = useCallback(
    (_payload: Record<string, unknown>, chartIndex: number) => {
      const { point, index } = resolveSourcePoint(chartIndex)
      if (!point || index === null) return
      setLocked(true)
      onLockedChange?.(true)
      onLockChange?.(true, point, index)
      onDotClick?.(point, index)
    },
    [onDotClick, onLockChange, onLockedChange, resolveSourcePoint],
  )

  const renderVisibleDot = useCallback(
    (props: unknown) => {
      const dotProps = props as { cx?: number; cy?: number }
      if (typeof dotProps.cx !== 'number' || typeof dotProps.cy !== 'number') return null
      return <circle cx={dotProps.cx} cy={dotProps.cy} r={3} fill={color} strokeWidth={0} />
    },
    [color],
  )

  const lineDot = showDots ? renderVisibleDot : false

  /** Tooltip do Recharts re-renderiza o SVG a cada mousemove; com sync de hover usamos só o header. */
  const showRechartsTooltip = showTooltip && !onHoverPoint

  const tooltipContent = useMemo(() => {
    if (CustomTooltip) {
      return (props: Record<string, unknown>) => <CustomTooltip {...props} />
    }
    return (props: {
      active?: boolean
      payload?: readonly TooltipEntry[]
      label?: unknown
    }) => (
      <DefaultTooltip
        active={props.active}
        payload={props.payload}
        label={props.label}
        formatter={tooltipFormatter}
        labelFormatter={tooltipLabelFormatter}
      />
    )
  }, [CustomTooltip, tooltipFormatter, tooltipLabelFormatter])

  return (
    <div className="relative select-none" style={{ height }}>
      {locked && <div className="absolute inset-0 bg-white/60 z-10" aria-hidden />}
      <ResponsiveContainer width="100%" height="100%">
        <LineChart
          data={displayData}
          margin={margin}
          onMouseDown={handleChartMouseDown}
          onMouseMove={handleChartMouseMove}
          onMouseUp={handleChartMouseUp}
          onMouseLeave={handleMouseLeave}
          onDoubleClick={handleReset}
        >
          {showGrid && (
            <CartesianGrid strokeDasharray="4 4" stroke="#e5e7eb" vertical={false} />
          )}

          <XAxis
            dataKey={xAxisKey}
            tick={{ fontSize: 11, fill: '#6b7280' }}
            axisLine={{ stroke: '#e5e7eb' }}
            tickLine={false}
            tickFormatter={xAxisFormatter}
            height={32}
          >
            {xAxisLabel && (
              <Label
                value={xAxisLabel}
                offset={-8}
                position="insideBottom"
                style={{ fontSize: 11, fill: '#9ca3af' }}
              />
            )}
          </XAxis>

          <YAxis
            tick={{ fontSize: 11, fill: '#6b7280' }}
            axisLine={false}
            tickLine={false}
            width={yAxisWidth}
            tickFormatter={yAxisFormatter}
            domain={yAxisDomain}
          >
            {yAxisLabel && (
              <Label
                value={yAxisLabel}
                angle={-90}
                position="insideLeft"
                offset={10}
                style={{ fontSize: 11, fill: '#6b7280', textAnchor: 'middle' }}
              />
            )}
          </YAxis>

          {showRechartsTooltip && (
            <Tooltip
              cursor={{ stroke: '#d1d5db', strokeWidth: 1 }}
              isAnimationActive={false}
              content={tooltipContent}
            />
          )}

          {selectionMin !== null && selectionMax !== null && selectionMax > selectionMin && (
            <ReferenceArea
              x1={
                (displayData[selectionMin] as Record<string, unknown> | undefined)?.[
                xAxisKey
                ] as string | number | undefined
              }
              x2={
                (displayData[selectionMax] as Record<string, unknown> | undefined)?.[
                xAxisKey
                ] as string | number | undefined
              }
              stroke={color}
              strokeOpacity={0.35}
              fill={color}
              fillOpacity={0.12}
            />
          )}

          <Line
            type={curveType}
            dataKey={dataKey}
            stroke={color}
            strokeWidth={lineWidth}
            dot={lineDot}
            activeDot={
              onDotClick
                ? {
                  r: 5,
                  fill: color,
                  strokeWidth: 2,
                  stroke: '#fff',
                  cursor: 'pointer',
                  onClick: (_: unknown, payload: unknown) => {
                    if (
                      payload &&
                      typeof payload === 'object' &&
                      'payload' in (payload as object) &&
                      'index' in (payload as object)
                    ) {
                      const p = payload as {
                        payload: Record<string, unknown>
                        index: number
                      }
                      handleDotClick(p.payload, p.index)
                    }
                  },
                }
                : { r: 5, fill: color, strokeWidth: 2, stroke: '#fff' }
            }
            isAnimationActive={false}
          />

          {showBrush && (
            <Brush
              dataKey={xAxisKey}
              startIndex={brushStart}
              endIndex={brushEnd}
              onChange={handleBrushChange}
              height={brushHeight}
              stroke="#d1d5db"
              fill="#f9fafb"
              travellerWidth={10}
              tickFormatter={xAxisFormatter}
            >
              <LineChart>
                <Line
                  dataKey={dataKey}
                  stroke={color}
                  strokeWidth={1}
                  dot={false}
                  isAnimationActive={false}
                />
              </LineChart>
            </Brush>
          )}
        </LineChart>
      </ResponsiveContainer>
    </div>
  )
})

export const SpeedChartPlot = memo(SpeedChartPlotComponent)
